import { useState, useEffect, useMemo } from "react";
import { ClipboardList, Plus } from "lucide-react";
import { ContainerBloco } from "../Common/styles";
import { Container, TituloBlocoPendencias } from "./styles";
import { comum } from "../../../styles/Themes/light";
import KanbanPendencias from "../../Pendencias/Kanban";
import { ButtonNovaPendencia } from "../../Pendencias/Kanban/styles";
import ModalCriarPendencia from "../../Pendencias/Kanban/ModalCriarPendencia";
import { useAuth } from "../../../contexts/UserContext";
import api from "../../../services/api";
import toast from "react-hot-toast";
import { toastDefsError } from "../../../config/toastConfig";
import AbaErro from "../Common/AbaErro";
import AbaAcessoNegado from "../Common/AbaAcessoNegado";
import { isAcessoNegado } from "../Common/utils";

interface AbaPendenciasProps {
    abaAtiva: string;
    id_proposta: number;
    acessoNegado?: boolean;
    onPendenciaAtualizada?: () => void;
    onNavigateToFile?: (fileId: number) => void;
}

export default function AbaPendencias({ abaAtiva, id_proposta, acessoNegado, onPendenciaAtualizada, onNavigateToFile }: AbaPendenciasProps) {
    const [isModalCriarOpen, setIsModalCriarOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [propostaData, setPropostaData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const { user } = useAuth();

    const [acessoNegadoLocal, setAcessoNegadoLocal] = useState(false);

    const carregarProposta = async () => {
        if (!id_proposta) return;
        
        setLoading(true);
        setError(false);
        setAcessoNegadoLocal(false);
        try {
            const response = await api.get(`/proposta/${id_proposta}`);
            if (response.data.status === 'success') {
                setPropostaData(response.data.result);
            } else {
                const errorMessage = response.data.message || '';
                if (isAcessoNegado({ message: errorMessage })) {
                    setAcessoNegadoLocal(true);
                } else {
                    setError(true);
                }
            }
        } catch (error: any) {
            console.error('Erro ao carregar proposta:', error);
            if (isAcessoNegado(error)) {
                setAcessoNegadoLocal(true);
            } else {
                setError(true);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (abaAtiva === 'pendencias' && id_proposta) {
            carregarProposta();
        }
    }, [abaAtiva, id_proposta]);

    const isAdminOuOperador = useMemo(() => {
        return user?.profile === 'admin' || user?.profile === 'operador';
    }, [user?.profile]);

    const temSolicitante = useMemo(() => {
        if (!propostaData) return false;
        return !!(
            propostaData.id_escritorio_solicitante && 
            propostaData.nome_escritorio
        );
    }, [propostaData]);

    const temProfissional = useMemo(() => {
        if (!propostaData) return false;
        return !!(
            propostaData.id_usuario && 
            (propostaData.nomeProfissional || propostaData.nome_profissional)
        );
    }, [propostaData]);

    if (abaAtiva !== 'pendencias') {
        return null;
    }

    if (acessoNegado || acessoNegadoLocal) {
        return <AbaAcessoNegado />;
    }

    if (error) {
        return <AbaErro mensagem="Não foi possível carregar pendências." onRetry={carregarProposta} />;
    }

    const handleNovaPendencia = () => {
        if (isAdminOuOperador && !temSolicitante && !temProfissional) {
            toast.error(
                'Não é possível criar uma pendência. Demanda não possui solicitante ou profissional cadastrado.',
                toastDefsError
            );
            return;
        }
        setIsModalCriarOpen(true);
    };

    const handlePendenciaCriada = () => {
        setRefreshKey(prev => prev + 1);
        onPendenciaAtualizada?.();
    };

    return (
        <Container>
            <ContainerBloco>
                <TituloBlocoPendencias>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <ClipboardList size={16} color={comum.colors.primaryRed400}/>
                        Pendências
                    </div>
                    <ButtonNovaPendencia onClick={handleNovaPendencia}>
                        <Plus size={16} />
                        Nova Pendência
                    </ButtonNovaPendencia>
                </TituloBlocoPendencias>
                <KanbanPendencias 
                    key={refreshKey} 
                    id_proposta={id_proposta} 
                    onRefresh={() => {
                        handlePendenciaCriada();
                        onPendenciaAtualizada?.();
                    }}
                    onNavigateToFile={onNavigateToFile}
                />
            </ContainerBloco>

            <ModalCriarPendencia
                modalAtivo={isModalCriarOpen}
                setModalAtivo={setIsModalCriarOpen}
                id_proposta={id_proposta}
                onPendenciaCriada={handlePendenciaCriada}
            />
        </Container>
    );
}
